import AdminScreen from 'components/adminScreen';
import React, { useEffect, useState, useCallback } from 'react';
import classes from 'styles/RoutingEngine.module.scss';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAdmin } from 'context/admin';
import { ROUTES } from 'config';

const VENDOR_NAMES = ['terrapay', 'lightnet', 'morepay', 'cellulant', 'digit9', 'muthoot', 'fleximoney', 'eficyent'];

interface VendorHealth {
	isHealthy: boolean;
	lastCheck: string;
	responseTime?: number;
	errorCount: number;
	isManuallyDeactivated?: boolean;
	uptime24h?: number;
	successRate24h?: number;
}

interface CountryRoutingEntry {
	BANK: { vendors: string[]; source: 'dynamic' | 'static' };
	WALLET: { vendors: string[]; source: 'dynamic' | 'static' };
}

export default function PRoutingEngine() {
	const { Atoken, headers } = useAdmin();

	const [vendorHealth, setVendorHealth] = useState<Record<string, VendorHealth>>({});
	const [routingTable, setRoutingTable] = useState<Record<string, CountryRoutingEntry>>({});
	const [loading, setLoading] = useState(false);

	// Edit state
	const [editingRow, setEditingRow] = useState<{ country: string; type: 'BANK' | 'WALLET' } | null>(null);
	const [editVendors, setEditVendors] = useState<string[]>([]);

	// Add new country state
	const [addingNew, setAddingNew] = useState(false);
	const [newCountry, setNewCountry] = useState('');
	const [newType, setNewType] = useState<'BANK' | 'WALLET'>('BANK');
	const [newVendors, setNewVendors] = useState('');

	const authHeaders = { Authorization: `Bearer ${Atoken}`, ...headers };

	const fetchAll = useCallback(async () => {
		setLoading(true);
		try {
			const [healthRes, routingRes] = await Promise.all([
				axios.get(ROUTES.RoutingEngine_VendorHealth, { headers: authHeaders }),
				axios.get(ROUTES.RoutingEngine_CountryRouting, { headers: authHeaders }),
			]);
			setVendorHealth(healthRes.data?.data || {});
			setRoutingTable(routingRes.data?.data || {});
		} catch (e: any) {
			toast.error('Failed to load routing engine data');
		} finally {
			setLoading(false);
		}
	}, [Atoken]);

	useEffect(() => {
		fetchAll();
	}, [fetchAll]);

	const activateVendor = async (vendor: string) => {
		try {
			await axios.put(`${ROUTES.RoutingEngine_Base}/vendor/${vendor}/activate`, {}, { headers: authHeaders });
			toast.success(`${vendor} activated`);
			fetchAll();
		} catch {
			toast.error(`Failed to activate ${vendor}`);
		}
	};

	const deactivateVendor = async (vendor: string) => {
		try {
			await axios.put(`${ROUTES.RoutingEngine_Base}/vendor/${vendor}/deactivate`, {}, { headers: authHeaders });
			toast.success(`${vendor} deactivated`);
			fetchAll();
		} catch {
			toast.error(`Failed to deactivate ${vendor}`);
		}
	};

	const moveVendor = (index: number, direction: -1 | 1) => {
		const next = index + direction;
		if (next < 0 || next >= editVendors.length) return;
		const updated = [...editVendors];
		[updated[index], updated[next]] = [updated[next], updated[index]];
		setEditVendors(updated);
	};

	const saveRouting = async (countryCode: string, transferType: 'BANK' | 'WALLET', vendorsInput: string[] | string) => {
		if (!/^[A-Z]{2,3}$/.test(countryCode)) {
			toast.error('Country code must be 2–3 uppercase letters (e.g. NG, AED)');
			return;
		}
		const vendors = Array.isArray(vendorsInput)
			? vendorsInput
			: vendorsInput.split(',').map(v => v.trim()).filter(Boolean);
		if (vendors.length === 0) {
			toast.error('Enter at least one vendor');
			return;
		}
		const invalid = vendors.filter(v => !VENDOR_NAMES.includes(v));
		if (invalid.length > 0) {
			toast.error(`Unknown vendor(s): ${invalid.join(', ')}. Valid: ${VENDOR_NAMES.join(', ')}`);
			return;
		}
		if (addingNew && routingTable[countryCode]?.[transferType]) {
			toast.error(`${countryCode}/${transferType} already exists — use Edit instead`);
			return;
		}
		try {
			await axios.put(
				`${ROUTES.RoutingEngine_Base}/country/${countryCode}/routing`,
				{ transferType, vendors },
				{ headers: { 'Content-Type': 'application/json', ...authHeaders } },
			);
			toast.success(`Routing updated for ${countryCode}/${transferType}`);
			setEditingRow(null);
			setAddingNew(false);
			setNewCountry('');
			setNewVendors('');
			fetchAll();
		} catch {
			toast.error('Failed to update routing');
		}
	};

	const resetRouting = async (countryCode: string, transferType: 'BANK' | 'WALLET') => {
		try {
			await axios.delete(
				`${ROUTES.RoutingEngine_Base}/country/${countryCode}/routing?transferType=${transferType}`,
				{ headers: authHeaders },
			);
			toast.success(`${countryCode}/${transferType} reset to static default`);
			fetchAll();
		} catch {
			toast.error('Failed to reset routing');
		}
	};

	const resetAll = async () => {
		if (!window.confirm('Reset ALL dynamic routing overrides to static defaults?')) return;
		try {
			await axios.delete(`${ROUTES.RoutingEngine_Base}/country-routing/reset-all`, { headers: authHeaders });
			toast.success('All dynamic routing cleared');
			fetchAll();
		} catch {
			toast.error('Failed to reset all routing');
		}
	};

	const healthEntries = Object.entries(vendorHealth);
	const routingEntries = Object.entries(routingTable).sort(([a], [b]) => a.localeCompare(b));

	return (
		<AdminScreen className={classes.container}>
			{/* ── Vendor Health ───────────────────────────── */}
			<section className={classes.section}>
				<div className={classes.sectionHeader}>
					<h2>Vendor Health</h2>
					<button className={classes.refreshBtn} onClick={fetchAll} disabled={loading}>
						{loading ? 'Loading...' : 'Refresh'}
					</button>
				</div>
				<div className={classes.tableWrap}>
					<table>
						<thead>
							<tr>
								<th>Vendor</th>
								<th>Status</th>
								<th>Response Time</th>
								<th>Success Rate 24h</th>
								<th>Error Count</th>
								<th>Manual State</th>
								<th>Action</th>
							</tr>
						</thead>
						<tbody>
							{healthEntries.length === 0 ? (
								<tr><td colSpan={7}>{loading ? 'Loading...' : 'No data'}</td></tr>
							) : (
								healthEntries.map(([vendor, h]) => (
									<tr key={vendor}>
										<td className={classes.vendorName}>{vendor}</td>
										<td>
											<span className={h.isHealthy ? classes.healthy : classes.unhealthy}>
												{h.isHealthy ? 'Healthy' : 'Unhealthy'}
											</span>
										</td>
										<td>{h.responseTime != null ? `${h.responseTime}ms` : '—'}</td>
										<td>{h.successRate24h != null ? `${h.successRate24h}%` : '—'}</td>
										<td>{h.errorCount}</td>
										<td>
											<span className={h.isManuallyDeactivated ? classes.deactivated : classes.active}>
												{h.isManuallyDeactivated ? 'Deactivated' : 'Active'}
											</span>
										</td>
										<td>
											{h.isManuallyDeactivated ? (
												<button className={classes.activateBtn} onClick={() => activateVendor(vendor)}>
													Activate
												</button>
											) : (
												<button className={classes.deactivateBtn} onClick={() => deactivateVendor(vendor)}>
													Deactivate
												</button>
											)}
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			</section>

			{/* ── Country Routing Table ────────────────────── */}
			<section className={classes.section}>
				<div className={classes.sectionHeader}>
					<h2>Country Routing</h2>
					<div className={classes.headerActions}>
						<button className={classes.addBtn} onClick={() => { setAddingNew(true); setEditingRow(null); }}>
							+ Add Country Override
						</button>
						<button className={classes.resetAllBtn} onClick={resetAll}>
							Reset All to Static
						</button>
					</div>
				</div>

				{addingNew && (
					<div className={classes.addForm}>
						<span className={classes.formLabel}>Country Code:</span>
						<input
							className={classes.input}
							placeholder="e.g. NG"
							value={newCountry}
							onChange={e => setNewCountry(e.target.value.toUpperCase())}
							maxLength={3}
						/>
						<span className={classes.formLabel}>Type:</span>
						<select className={classes.select} value={newType} onChange={e => setNewType(e.target.value as any)}>
							<option value="BANK">BANK</option>
							<option value="WALLET">WALLET</option>
						</select>
						<span className={classes.formLabel}>Vendors (comma-separated, in priority order):</span>
						<input
							className={classes.inputWide}
							placeholder="e.g. digit9, terrapay"
							value={newVendors}
							onChange={e => setNewVendors(e.target.value)}
						/>
						<button className={classes.saveBtn} onClick={() => saveRouting(newCountry, newType, newVendors)}>
							Save
						</button>
						<button className={classes.cancelBtn} onClick={() => { setAddingNew(false); setNewCountry(''); setNewVendors(''); }}>
							✖
						</button>
					</div>
				)}

				<div className={classes.tableWrap}>
					<table>
						<thead>
							<tr>
								<th>Country</th>
								<th>Type</th>
								<th>Vendors (priority order)</th>
								<th>Source</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{routingEntries.length === 0 ? (
								<tr><td colSpan={5}>{loading ? 'Loading...' : 'No data'}</td></tr>
							) : (
								routingEntries.flatMap(([country, entry]) =>
									(['BANK', 'WALLET'] as const).map(type => {
										const info = entry[type];
										const isEditing = editingRow?.country === country && editingRow?.type === type;
										return (
											<tr key={`${country}-${type}`}>
												<td className={classes.countryCode}>{country}</td>
												<td>{type}</td>
												<td>
													{isEditing ? (
														<span className={classes.vendorList}>
															{editVendors.map((v, i) => (
																<span key={v} className={classes.vendorEditTag}>
																	<button className={classes.arrowBtn} onClick={() => moveVendor(i, -1)} disabled={i === 0}>↑</button>
																	<span className={classes.vendorTag}>{i + 1}. {v}</span>
																	<button className={classes.arrowBtn} onClick={() => moveVendor(i, 1)} disabled={i === editVendors.length - 1}>↓</button>
																	<button className={classes.removeVendorBtn} onClick={() => setEditVendors(editVendors.filter((_, idx) => idx !== i))}>×</button>
																</span>
															))}
															{VENDOR_NAMES.filter(v => !editVendors.includes(v)).length > 0 && (
																<select
																	className={classes.addVendorSelect}
																	value=""
																	onChange={e => { if (e.target.value) setEditVendors([...editVendors, e.target.value]); }}
																>
																	<option value="">+ Add vendor</option>
																	{VENDOR_NAMES.filter(v => !editVendors.includes(v)).map(v => (
																		<option key={v} value={v}>{v}</option>
																	))}
																</select>
															)}
														</span>
													) : (
														<span className={classes.vendorList}>
															{info.vendors.map((v, i) => (
																<span key={v} className={classes.vendorTag}>
																	{i + 1}. {v}
																</span>
															))}
														</span>
													)}
												</td>
												<td>
													<span className={info.source === 'dynamic' ? classes.dynamic : classes.static}>
														{info.source}
													</span>
												</td>
												<td className={classes.actions}>
													{isEditing ? (
														<>
															<button className={classes.saveBtn} onClick={() => saveRouting(country, type, editVendors)}>
																Save
															</button>
															<button className={classes.cancelBtn} onClick={() => { setEditingRow(null); setEditVendors([]); }}>
																✖
															</button>
														</>
													) : (
														<>
															<button
																className={classes.editBtn}
																onClick={() => {
																	setEditingRow({ country, type });
																	setEditVendors([...info.vendors]);
																	setAddingNew(false);
																}}
															>
																Edit
															</button>
															{info.source === 'dynamic' && (
																<button
																	className={classes.resetBtn}
																	onClick={() => resetRouting(country, type)}
																>
																	Reset
																</button>
															)}
														</>
													)}
												</td>
											</tr>
										);
									})
								)
							)}
						</tbody>
					</table>
				</div>
			</section>
		</AdminScreen>
	);
}
